import React, { useState } from 'react'
import { Alert } from 'react-bootstrap'
import {
  useDeleteDocument,
  extractDocumentError,
  type Document,
} from '../../services/documents'
import { Modal } from '../UI/Modal/Modal'
import { AlertTriangle } from 'lucide-react'

interface DeleteDocumentConfirmationProps {
  document: Document
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const DeleteDocumentConfirmation: React.FC<
  DeleteDocumentConfirmationProps
> = ({ document, isOpen, onClose, onSuccess }) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const deleteMutation = useDeleteDocument()

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await deleteMutation.mutateAsync({ id: document.id })
      onSuccess?.()
      onClose()
    } catch (err) {
      setError(extractDocumentError(err))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
      size="md"
      title="Delete Document"
      onAccept={handleDelete}
      acceptButtonText="Delete"
      acceptButtonProps={{ variant: 'danger' }}
      blockAccept={isDeleting}
      loading={isDeleting}
      onCancel={handleClose}
      cancelButtonText="Cancel"
    >
      <div className="space-y-4">
        {/* Warning Icon & Message */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <AlertTriangle className="text-red-600" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-gray-900">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{document.name}</span>?
            </p>
            <p className="text-sm text-gray-600 mt-2">
              This action cannot be undone. The document file will be
              permanently removed from storage.
            </p>
          </div>
        </div>

        {/* Document Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
          <div className="text-sm">
            <span className="text-gray-600">Name:</span>{' '}
            <span className="text-gray-900">{document.name}</span>
          </div>
          {document.product_name && (
            <div className="text-sm">
              <span className="text-gray-600">Product:</span>{' '}
              <span className="text-gray-900">{document.product_name}</span>
            </div>
          )}
          {document.variant_name && (
            <div className="text-sm">
              <span className="text-gray-600">Variant:</span>{' '}
              <span className="text-gray-900">{document.variant_name}</span>
            </div>
          )}
          {document.contact_company_name && (
            <div className="text-sm">
              <span className="text-gray-600">Company:</span>{' '}
              <span className="text-gray-900">
                {document.contact_company_name}
              </span>
            </div>
          )}
          <div className="text-sm">
            <span className="text-gray-600">Size:</span>{' '}
            <span className="text-gray-900">
              {((document.size ?? 0) / 1024 / 1024).toFixed(2)} MB
            </span>
          </div>
        </div>

        {/* Error Display */}
        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  )
}
